import { ABBR_MONTHS } from '@constants/date.js'
import { isTimetoastTemporal } from '@base/timetoast_temporal.js'
import { isTemporalPlainDateOrZonedDateTime, isTemporalInstant } from '@utils/temporal.js'

// Note: We used to use a switch statement on obj.constructor.name to figure out type of temporal class, but this gets mangled during minification

export default class TimetoastDateFormatter {
  static day(obj, timeZone = 'UTC', calendar = 'gregory') {
    let result = ''
    const temporalObj = isTimetoastTemporal(obj) ? obj.temporal : obj
    if (isTemporalPlainDateOrZonedDateTime(temporalObj)) {
      result = temporalObj.day
    } else if (isTemporalInstant(temporalObj)) {
      result = temporalObj.toZonedDateTimeISO(timeZone).withCalendar(calendar).day
    } else {
      throw new Error(`Unsupported temporal class : ${temporalObj}`)
    }
    return result.toString()
  }

  // Note: includeEra displays BCE, or in the case of year 1 it displays 1 CE
  static year(obj, timeZone = 'UTC', calendar = 'gregory', includeEra = true) {
    let result = ''
    const temporalObj = isTimetoastTemporal(obj) ? obj.temporal : obj
    let year, eraYear
    if (isTemporalPlainDateOrZonedDateTime(temporalObj)) {
      year = temporalObj.year
      eraYear = year <= 0 ? year - 1 : year
    } else if (isTemporalInstant(temporalObj)) {
      const zonedDateTime = temporalObj.toZonedDateTimeISO(timeZone).withCalendar(calendar)
      year = zonedDateTime.year
      eraYear = zonedDateTime.eraYear
    } else {
      throw new Error(`Unsupported temporal class : ${temporalObj}`)
    }
    const absEraYear = Math.abs(eraYear)
    const showYearDelimeter = absEraYear.toString().length > 4
    switch (Math.sign(year)) {
      case 1:
        // Note: 1 AD/CE, specifically, should be shown with the CE text
        if (includeEra && year === 1) {
          result = `1 CE`
        } else {
          result = showYearDelimeter ? year.toLocaleString() : year.toString()
        }
        break

      case 0:
      case -1: {
        const yearString = showYearDelimeter ? absEraYear.toLocaleString() : absEraYear.toString()
        if (includeEra) {
          result = `${yearString} BCE`
        } else {
          result = yearString
        }
        break
      }
    }
    return result
  }

  static abbreviatedMonth(obj, timeZone = 'UTC', calendar = 'gregory') {
    let result = ''
    const temporalObj = isTimetoastTemporal(obj) ? obj.temporal : obj
    let monthIndex
    // Note: Temporal months start at 1
    if (isTemporalPlainDateOrZonedDateTime(temporalObj)) {
      monthIndex = temporalObj.month - 1
    } else if (isTemporalInstant(temporalObj)) {
      monthIndex = temporalObj.toZonedDateTimeISO(timeZone).withCalendar(calendar).month - 1
    } else {
      throw new Error(`Unsupported temporal class : ${temporalObj}`)
    }
    result = ABBR_MONTHS[monthIndex]
    return result
  }

  static humanDate(obj, timeZone = 'UTC', calendar = 'gregory', formatString = 'MMM d, Y') {
    let result = ''
    let temporalObj = obj
    if (isTimetoastTemporal(obj)) {
      if (obj.yearOnly) {
        return TimetoastDateFormatter.year(obj)
      }
      temporalObj = obj.temporal
    }
    let day, eraYear
    if (isTemporalPlainDateOrZonedDateTime(temporalObj)) {
      day = temporalObj.day
      const year = temporalObj.year
      eraYear = year <= 0 ? year - 1 : year
    } else if (isTemporalInstant(temporalObj)) {
      const formattable = temporalObj.toZonedDateTimeISO(timeZone).withCalendar(calendar)
      day = formattable.day
      eraYear = formattable.eraYear
    } else {
      throw new Error(`Unsupported temporal class : ${temporalObj}`)
    }
    result = `${TimetoastDateFormatter.abbreviatedMonth(obj)} ${day}, ${Math.abs(eraYear)}`
    return result
  }
}
